import {
  EMPLOYMENT_STATUS,
  PARTS_ACTION_STATUS,
  PARTS_REQUEST_TYPE,
  PARTS_STATUS,
  RO_LIFECYCLE,
  RO_STATUS,
  TRANSPORT_TYPES,
  WORK_MIX,
} from "../constants/roStatus";

export function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

export function safeNumber(v) {
  const n =
    typeof v === "number"
      ? v
      : v === "" || v == null
      ? NaN
      : Number(v);
  return Number.isFinite(n) ? n : null;
}

export function nowISO() {
  return new Date().toISOString();
}

export function toISODate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function isoDateKeyInTimeZone(timeZone, date = new Date()) {
  const tz = String(timeZone || "").trim() || "America/Los_Angeles";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    const yyyy = get("year");
    const mm = get("month");
    const dd = get("day");

    if (yyyy && mm && dd) return `${yyyy}-${mm}-${dd}`;
  } catch {
    // ignore
  }
  return toISODate(date);
}

export function isTodayISO(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export function genId() {
  return `ro_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function msBetween(aISO, bISO) {
  const a = aISO ? new Date(aISO).getTime() : NaN;
  const b = bISO ? new Date(bISO).getTime() : NaN;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.max(0, b - a);
}

export function secBetween(aISO, bISO) {
  return Math.floor(msBetween(aISO, bISO) / 1000);
}

export function addEvent(ro, type, meta = {}) {
  const evt = { ts: nowISO(), type, meta };
  const historyEvents = Array.isArray(ro?.historyEvents) ? ro.historyEvents : [];
  return { ...ro, historyEvents: [...historyEvents, evt] };
}

export function normalizeEmploymentStatus(rawStatus, rawActive) {
  const s = safeString(rawStatus).toUpperCase().trim();

  if (s === EMPLOYMENT_STATUS.TERMINATED) return EMPLOYMENT_STATUS.TERMINATED;
  if (s === EMPLOYMENT_STATUS.SUSPENDED) return EMPLOYMENT_STATUS.SUSPENDED;
  if (s === EMPLOYMENT_STATUS.ACTIVE) return EMPLOYMENT_STATUS.ACTIVE;

  if (s === "INACTIVE") return EMPLOYMENT_STATUS.SUSPENDED;
  if (s === "REMOVED") return EMPLOYMENT_STATUS.TERMINATED;
  if (s === "TERMINATE" || s === "TERMINATED_EMPLOYEE") {
    return EMPLOYMENT_STATUS.TERMINATED;
  }

  if (rawActive === false) return EMPLOYMENT_STATUS.SUSPENDED;

  return EMPLOYMENT_STATUS.ACTIVE;
}

export function normalizeTagNumber(v) {
  const raw = safeString(v).trim();
  if (!raw) return "";
  return raw.toUpperCase();
}

export function normalizeVinLast8(v) {
  return safeString(v).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(-8);
}

function firstNonBlank(...values) {
  for (const value of values) {
    const s = safeString(value).trim();
    if (s) return s;
  }
  return "";
}

export function getTagForUniq(r) {
  return normalizeTagNumber(
    firstNonBlank(
      r?.tagNumber,
      r?.tag_number,
      r?.tag,
      r?.keyTag,
      r?.key_tag,
      r?.tagId,
      r?.tag_id
    )
  );
}

export function normalizeTransportType(v) {
  const s = safeString(v).toUpperCase();
  if (s === "WAITER") return TRANSPORT_TYPES.WAITER;
  if (s === "LOANER") return TRANSPORT_TYPES.LOANER;
  if (s === "SHUTTLE") return TRANSPORT_TYPES.SHUTTLE;
  return TRANSPORT_TYPES.OTHER;
}

export function normalizeWorkMix(v) {
  const s = safeString(v).toUpperCase();
  if (s === "MAINTENANCE") return WORK_MIX.MAINTENANCE;
  if (s === "DIAGNOSIS") return WORK_MIX.DIAGNOSIS;
  if (s === "OTHER") return WORK_MIX.OTHER;
  if (s === "MAINT" || s === "PM" || s === "SERVICE") {
    return WORK_MIX.MAINTENANCE;
  }
  if (s === "DIAG" || s === "DIAGNOSTIC") {
    return WORK_MIX.DIAGNOSIS;
  }
  return WORK_MIX.OTHER;
}

export function normalizePartsActionStatus(v) {
  const s = safeString(v).toUpperCase().trim();

  if (!s || s === "NONE" || s === "CLEAR") return "NONE";

  if (s === "REVIEW" || s === "OPEN" || s === "REOPEN") {
    return PARTS_ACTION_STATUS.REVIEW;
  }

  if (s === "PRE_PULLED" || s === "PREPULLED") {
    return PARTS_ACTION_STATUS.PRE_PULLED;
  }

  if (s === "NO_ACTION") return PARTS_ACTION_STATUS.NO_ACTION;
  if (s === "ACTIONED") return PARTS_ACTION_STATUS.PRE_PULLED;

  return "NONE";
}

export function normalizeStatus(v) {
  const s = safeString(v).toUpperCase().trim();

  if (Object.prototype.hasOwnProperty.call(RO_STATUS, s)) return RO_STATUS[s];

  if (s === "UNASSIGNED") return RO_STATUS.AVAILABLE;
  if (s === "AVAILABLE") return RO_STATUS.AVAILABLE;
  if (s === "ASSIGNED") return RO_STATUS.ASSIGNED;
  if (s === "IN_PROGRESS") return RO_STATUS.IN_PROGRESS;
  if (s === "REPAIRS_COMPLETE" || s === "REPAIRS COMPLETE") return RO_STATUS.COMPLETED;
  if (s === "COMPLETED" || s === "DONE") return RO_STATUS.COMPLETED;
  if (s === "ON_HOLD" || s === "ON HOLD") return "ON_HOLD";
  if (s === "VEHICLE_GONE" || s === "VEHICLE GONE") return "VEHICLE_GONE";

  return RO_STATUS.AVAILABLE;
}

export function normalizePartsStatus(v) {
  const s = safeString(v).toUpperCase();
  if (s === "NONE") return PARTS_STATUS.NONE;
  if (s === "PULLED") return PARTS_STATUS.PULLED;
  if (s === "ORDERED") return PARTS_STATUS.ORDERED;
  if (s === "ARRIVED") return PARTS_STATUS.ARRIVED;
  if (s === "QUOTED") return PARTS_STATUS.QUOTED;
  if (s === "NEEDED") return PARTS_STATUS.NONE;
  if (s === "WAITING") return PARTS_STATUS.ORDERED;
  return PARTS_STATUS.NONE;
}

export function normalizeLifecycle(v) {
  const s = safeString(v).toUpperCase();
  if (Object.prototype.hasOwnProperty.call(RO_LIFECYCLE, s)) {
    return RO_LIFECYCLE[s];
  }

  if (s === "WAITING_ON_PARTS" || s === "WAITINGPARTS" || s === "WAITING_ON_PART") {
    return RO_LIFECYCLE.WAITING_FOR_PARTS;
  }

  if (
    s === "WAITING_FOR_ASSIGNMENT" ||
    s === "WAITINGASSIGNMENT" ||
    s === "UNASSIGNED"
  ) {
    return RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;
  }

  if (s === "DONE" || s === "COMPLETE") return RO_LIFECYCLE.COMPLETED;

  return RO_LIFECYCLE.OPEN;
}

export function isArchived(ro) {
  return normalizeStatus(ro?.status) === RO_STATUS.ARCHIVED;
}

export function inferLifecycle(ro) {
  const t = ro?.timestamps || {};
  const workActive = !!t.workStartAt && !t.workEndAt;
  if (workActive) return RO_LIFECYCLE.IN_PROGRESS;

  const st = normalizeStatus(ro?.status);
  const hasExplicitLifecycle = Boolean(safeString(ro?.lifecycle || ro?.lifeCycle).trim());
  const explicit = normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle);

  if (st === RO_STATUS.ARCHIVED) {
    return hasExplicitLifecycle ? explicit : RO_LIFECYCLE.OPEN;
  }
  if (st === RO_STATUS.COMPLETED) return RO_LIFECYCLE.COMPLETED;

  const assignedRaw =
    firstNonBlank(
      ro?.assignedTechId,
      ro?.assigned_tech_id,
      ro?.techId,
      ro?.assignedToId,
      ro?.assignedTechName,
      ro?.assigned_tech_name,
      ro?.techName,
      ro?.assignedTo
    );

  const assigned = !!assignedRaw;

  if (!assigned) return RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;
  return RO_LIFECYCLE.OPEN;
}

export function statusFromLifecycle(lifecycle, ro) {
  const lc = normalizeLifecycle(lifecycle);
  const currentStatus = normalizeStatus(ro?.status);

  if (currentStatus === RO_STATUS.ARCHIVED) return RO_STATUS.ARCHIVED;
  if (currentStatus === "ON_HOLD") return "ON_HOLD";
  if (currentStatus === "VEHICLE_GONE") return "VEHICLE_GONE";

  if (lc === RO_LIFECYCLE.COMPLETED) return RO_STATUS.COMPLETED;
  if (lc === RO_LIFECYCLE.IN_PROGRESS) return RO_STATUS.IN_PROGRESS;
  if (lc === RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT) return RO_STATUS.AVAILABLE;
  if (lc === RO_LIFECYCLE.OPEN) return RO_STATUS.ASSIGNED;

  return RO_STATUS.AVAILABLE;
}

export function getLifecycle(ro) {
  return normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle || inferLifecycle(ro));
}

export function isWipRO(ro) {
  const st = normalizeStatus(ro?.status);
  return st !== RO_STATUS.COMPLETED && st !== "VEHICLE_GONE" && !isArchived(ro);
}

export function isCompletedRO(ro) {
  return normalizeStatus(ro?.status) === RO_STATUS.COMPLETED;
}

export function isWaitingForAssignment(ro) {
  return isWipRO(ro) && normalizeStatus(ro?.status) === RO_STATUS.AVAILABLE;
}

export function isWaitingForParts(ro) {
  return false;
}

export function isInProgressRO(ro) {
  return isWipRO(ro) && normalizeStatus(ro?.status) === RO_STATUS.IN_PROGRESS;
}

export function isOpenAssignedRO(ro) {
  if (!isWipRO(ro)) return false;
  const st = normalizeStatus(ro?.status);
  return st === RO_STATUS.ASSIGNED || st === RO_STATUS.IN_PROGRESS || st === "ON_HOLD";
}

export function validateTagNumberUnique(ros, tagNumber, excludeId = "") {
  const tag = normalizeTagNumber(tagNumber);
  if (!tag) return { ok: false, message: "Tag # is required." };

  const conflict = (Array.isArray(ros) ? ros : []).find((r) => {
    if (!r) return false;
    if (excludeId && r.id === excludeId) return false;
    if (!isWipRO(r)) return false;
    return getTagForUniq(r) === tag;
  });

  if (conflict) {
    const ref = safeString(conflict.roNumber || conflict.id);
    return {
      ok: false,
      message: `Tag # ${tag} is already in use on an OPEN RO (${ref}).`,
    };
  }

  return { ok: true, message: "" };
}

export function getAttentionFlags(ro, opts = {}) {
  const now = opts.nowISO || nowISO();
  const promiseISO = safeString(ro?.promiseTimeISO).trim();
  const promiseOverdue =
    !!promiseISO &&
    (() => {
      const p = new Date(promiseISO).getTime();
      const n = new Date(now).getTime();
      return Number.isFinite(p) && Number.isFinite(n) ? n > p : false;
    })();

  const tagMissing = !getTagForUniq(ro);

  return {
    promiseOverdue,
    waitingAssignmentTooLong: false,
    waitingPartsTooLong: false,
    tagMissing,
    waiterNoTech: false,
  };
}

export function normalizeRO(input) {
  const ro = input && typeof input === "object" ? input : {};

  const id = safeString(ro.id) || genId();
  const roNumber = firstNonBlank(
    ro.roNumber,
    ro.ro_number,
    ro.ro,
    ro.number,
    ro.repairOrderNumber
  );

  const createdAt =
    firstNonBlank(
      ro.timestamps?.createdAt,
      ro.createdAt,
      ro.created_at,
      ro.created
    ) || nowISO();

  const advisorName = firstNonBlank(
    ro.advisorName,
    ro.advisor_name,
    ro.advisor,
    ro.serviceAdvisor
  );
  const advisorEmploymentId = firstNonBlank(
    ro.advisorEmploymentId,
    ro.advisor_employment_id
  );

  const transportType = normalizeTransportType(
    firstNonBlank(
      ro.transportType,
      ro.transport_type,
      ro.transport,
      ro.transportation,
      ro.waiter ? "WAITER" : ""
    )
  );

  const workMix = normalizeWorkMix(
    firstNonBlank(
      ro.workMix,
      ro.work_mix,
      ro.workType,
      ro.jobCategory,
      ro.workCategory,
      ro.workMixType,
      "OTHER"
    )
  );

  const promiseTimeISO =
    firstNonBlank(ro.promiseTimeISO, ro.promise_time_iso, ro.promiseTime) || "";

  const partsStatus = normalizePartsStatus(
    firstNonBlank(ro.partsStatus, ro.parts_status)
  );
  const partsEtaISO = firstNonBlank(
    ro.partsEtaISO,
    ro.parts_eta_iso,
    ro.partsEta
  );
  const partsNotes = firstNonBlank(ro.partsNotes, ro.parts_notes);

  const concern = firstNonBlank(ro.concern, ro.complaint);
  const notes = firstNonBlank(ro.notes);

  const partsRequestType =
    safeString(
      firstNonBlank(ro.partsRequestType, ro.parts_request_type)
    ).toUpperCase() || "";
  const partsRequestNote = firstNonBlank(
    ro.partsRequestNote,
    ro.parts_request_note
  );
  const partsActionRequestedAt = firstNonBlank(
    ro.partsActionRequestedAt,
    ro.parts_action_requested_at
  );
  const partsActionRequestedByRole = firstNonBlank(
    ro.partsActionRequestedByRole,
    ro.parts_action_requested_by_role
  );
  const partsActionRequestedByName = firstNonBlank(
    ro.partsActionRequestedByName,
    ro.parts_action_requested_by_name
  );

  const partsActionStatus = normalizePartsActionStatus(
    firstNonBlank(ro.partsActionStatus, ro.parts_action_status)
  );
  const partsActionedAt = firstNonBlank(
    ro.partsActionedAt,
    ro.parts_actioned_at
  );
  const partsActionedByName = firstNonBlank(
    ro.partsActionedByName,
    ro.parts_actioned_by_name
  );

  const assignedTechId = firstNonBlank(
    ro.assignedTechId,
    ro.assigned_tech_id,
    ro.techId,
    ro.assignedToId
  );
  const assignedTechName = firstNonBlank(
    ro.assignedTechName,
    ro.assigned_tech_name,
    ro.techName,
    ro.assignedTo
  );

  const tagNumber = normalizeTagNumber(
    firstNonBlank(
      ro.tagNumber,
      ro.tag_number,
      ro.tag,
      ro.keyTag,
      ro.key_tag,
      ro.tagId,
      ro.tag_id
    )
  );

  const vinRaw = firstNonBlank(
    ro.vinLast8,
    ro.vin_last_8,
    ro.vinLast,
    ro.vin_last8,
    ro.vin
  );

  const vinLast8 = normalizeVinLast8(vinRaw);
  const vin = vinLast8;

  const tagMissing = !safeString(tagNumber).trim();

  const customerContacted =
    ro.customerContacted === true ||
    safeString(ro.customerContacted).toLowerCase().trim() === "true";

  const customerContactNote = firstNonBlank(
    ro.customerContactNote,
    ro.customer_contact_note
  );

  const customerContactedAt = firstNonBlank(
    ro.customerContactedAt,
    ro.customer_contacted_at
  );

  const customerContactedByRole = firstNonBlank(
    ro.customerContactedByRole,
    ro.customer_contacted_by_role
  );

  const customerContactedByName = firstNonBlank(
    ro.customerContactedByName,
    ro.customer_contacted_by_name
  );

  const timestamps = {
    createdAt,
    assignedAt: firstNonBlank(
      ro.timestamps?.assignedAt,
      ro.assignedAt,
      ro.assigned_at
    ),
    startedAt: firstNonBlank(
      ro.timestamps?.startedAt,
      ro.startedAt,
      ro.started_at
    ),
    waitingPartsAt: firstNonBlank(
      ro.timestamps?.waitingPartsAt,
      ro.waitingPartsAt,
      ro.waiting_parts_at
    ),
    completedAt: firstNonBlank(
      ro.timestamps?.completedAt,
      ro.completedAt,
      ro.completed_at
    ),
    archivedAt: firstNonBlank(
      ro.timestamps?.archivedAt,
      ro.archivedAt,
      ro.archived_at
    ),
    workStartAt: firstNonBlank(
      ro.timestamps?.workStartAt,
      ro.workStartAt,
      ro.work_start_at
    ),
    workEndAt: firstNonBlank(
      ro.timestamps?.workEndAt,
      ro.workEndAt,
      ro.work_end_at
    ),
    breakStartAt: firstNonBlank(
      ro.timestamps?.breakStartAt,
      ro.breakStartAt,
      ro.break_start_at
    ),
    breakEndAt: firstNonBlank(
      ro.timestamps?.breakEndAt,
      ro.breakEndAt,
      ro.break_end_at
    ),
    workedSeconds:
      safeNumber(ro.timestamps?.workedSeconds ?? ro.workedSeconds ?? ro.worked_seconds) ?? 0,
    breakSeconds:
      safeNumber(ro.timestamps?.breakSeconds ?? ro.breakSeconds ?? ro.break_seconds) ?? 0,
    sessionBreakSeconds:
      safeNumber(
        ro.timestamps?.sessionBreakSeconds ??
          ro.sessionBreakSeconds ??
          ro.session_break_seconds
      ) ?? 0,
    workSessions: Array.isArray(ro.timestamps?.workSessions || ro.workSessions)
      ? ro.timestamps?.workSessions || ro.workSessions
      : [],
  };

  const activeWorkStartedAtISO = firstNonBlank(
    ro.activeWorkStartedAtISO,
    ro.active_work_started_at_iso,
    ro.timestamps?.activeWorkStartedAtISO,
    ro.timestamps?.activeWorkStartedAt,
    ro.timestamps?.activeWorkStartedAtISO
  );

  const lastStoppedAtISO = firstNonBlank(
    ro.lastStoppedAtISO,
    ro.last_stopped_at_iso,
    ro.timestamps?.lastStoppedAtISO,
    ro.timestamps?.lastStoppedAt,
    ro.timestamps?.workEndAt
  );

  const totalWorkSeconds =
    safeNumber(
      ro.totalWorkSeconds ??
        ro.total_work_seconds ??
        ro.timestamps?.totalWorkSeconds ??
        ro.timestamps?.workedSeconds
    ) ?? 0;

  const completedHours =
    safeNumber(
      ro.completedHours ??
        ro.hoursCompleted ??
        ro.completed_hours ??
        ro.hours_completed
    ) ?? 0;

  const technicianCompletedAtISO = firstNonBlank(
    ro.technicianCompletedAtISO,
    ro.technician_completed_at_iso,
    ro.timestamps?.technicianCompletedAt,
    ro.timestamps?.completedAt
  );

  const flaggedHours = safeNumber(ro.flaggedHours ?? ro.flagged_hours);
  const flaggedByTechId = firstNonBlank(
    ro.flaggedByTechId,
    ro.flagged_by_tech_id
  );
  const flaggedByTechName = firstNonBlank(
    ro.flaggedByTechName,
    ro.flagged_by_tech_name
  );

  const manualFlag = safeString(firstNonBlank(ro.manualFlag, ro.manual_flag)).trim();
  const manualFlagTone = safeString(
    firstNonBlank(ro.manualFlagTone, ro.manual_flag_tone)
  )
    .trim()
    .toLowerCase();
  const manualFlagSetAt = safeString(
    firstNonBlank(ro.manualFlagSetAt, ro.manual_flag_set_at)
  ).trim();
  const manualFlagSetByRole = safeString(
    firstNonBlank(ro.manualFlagSetByRole, ro.manual_flag_set_by_role)
  ).trim();
  const manualFlagSetByName = safeString(
    firstNonBlank(ro.manualFlagSetByName, ro.manual_flag_set_by_name)
  ).trim();

  const lifecycleRaw = safeString(firstNonBlank(ro.lifecycle, ro.lifeCycle)).trim();
  const lifecycle = lifecycleRaw
    ? normalizeLifecycle(lifecycleRaw)
    : inferLifecycle({ ...ro, partsStatus, timestamps });

  const status = statusFromLifecycle(lifecycle, {
    ...ro,
    partsStatus,
    status: ro.status,
    timestamps,
  });

  const base = {
    id,
    roNumber,
    storeId: safeString(firstNonBlank(ro.storeId, ro.store_id, ro.store)).trim(),
    tagNumber,
    tagMissing,
    vin,
    vinLast8,
    customerName: firstNonBlank(
      ro.customerName,
      ro.customer_name,
      ro.customer
    ),
    vehicle: firstNonBlank(ro.vehicle, ro.unit),
    advisorName,
    advisorEmploymentId,
    concern,
    transportType,
    workMix,
    promiseTimeISO,
    lifecycle,
    status,
    assignedTechId,
    assignedTechName,
    partsStatus,
    partsEtaISO,
    partsNotes,
    partsRequestType:
      partsRequestType && Object.values(PARTS_REQUEST_TYPE).includes(partsRequestType)
        ? partsRequestType
        : "",
    partsRequestNote,
    partsActionRequestedAt,
    partsActionRequestedByRole,
    partsActionRequestedByName,
    partsActionStatus,
    partsActionedAt,
    partsActionedByName,
    customerContacted,
    customerContactNote,
    customerContactedAt,
    customerContactedByRole,
    customerContactedByName,
    flaggedHours: flaggedHours == null ? 0 : flaggedHours,
    flaggedByTechId,
    flaggedByTechName,
    manualFlag,
    manualFlagTone,
    manualFlagSetAt,
    manualFlagSetByRole,
    manualFlagSetByName,
    notes,
    timestamps,

    activeWorkStartedAtISO,
    active_work_started_at_iso: activeWorkStartedAtISO,
    lastStoppedAtISO,
    last_stopped_at_iso: lastStoppedAtISO,
    totalWorkSeconds,
    total_work_seconds: totalWorkSeconds,
    completedHours,
    hoursCompleted: completedHours,
    completed_hours: completedHours,
    technicianCompletedAtISO,
    technician_completed_at_iso: technicianCompletedAtISO,

    createdAt: timestamps.createdAt,
    assignedAt: timestamps.assignedAt,
    startedAt: timestamps.startedAt,
    waitingPartsAt: timestamps.waitingPartsAt,
    completedAt: timestamps.completedAt,
    archivedAt: timestamps.archivedAt,
    workStartAt: timestamps.workStartAt,
    workEndAt: timestamps.workEndAt,
    breakStartAt: timestamps.breakStartAt,
    breakEndAt: timestamps.breakEndAt,
    completedAtISO: firstNonBlank(
      ro.completedAtISO,
      ro.completed_at_iso,
      ro.timestamps?.completedAt,
      ro.completedAt
    ),
    historyEvents: Array.isArray(ro.historyEvents) ? ro.historyEvents : [],
  };

  if (!base.historyEvents.length) {
    return addEvent(base, "RO_IMPORTED", { fromLegacy: true });
  }

  return base;
}

export function canonicalizePatch(rawPatch) {
  const patch = rawPatch && typeof rawPatch === "object" ? { ...rawPatch } : {};

  const legacyTag = firstNonBlank(
    patch.tagNumber,
    patch.tag_number,
    patch.tag,
    patch.keyTag,
    patch.key_tag,
    patch.tagId,
    patch.tag_id
  );

  if (legacyTag) {
    patch.tagNumber = normalizeTagNumber(legacyTag);
    delete patch.tag_number;
    delete patch.tag;
    delete patch.keyTag;
    delete patch.key_tag;
    delete patch.tagId;
    delete patch.tag_id;
  }

  if (Object.prototype.hasOwnProperty.call(patch, "tagNumber")) {
    patch.tagNumber = normalizeTagNumber(patch.tagNumber);
    patch.tagMissing = !safeString(patch.tagNumber).trim();
  }

  if (
    Object.prototype.hasOwnProperty.call(patch, "vin") ||
    Object.prototype.hasOwnProperty.call(patch, "vinLast8") ||
    Object.prototype.hasOwnProperty.call(patch, "vin_last_8") ||
    Object.prototype.hasOwnProperty.call(patch, "vinLast") ||
    Object.prototype.hasOwnProperty.call(patch, "vin_last8")
  ) {
    const rawVin = firstNonBlank(
      patch.vinLast8,
      patch.vin_last_8,
      patch.vinLast,
      patch.vin_last8,
      patch.vin
    );
    const nextVin = normalizeVinLast8(rawVin);
    patch.vin = nextVin;
    patch.vinLast8 = nextVin;
    patch.vin_last_8 = nextVin;
    patch.vinLast = nextVin;
    patch.vin_last8 = nextVin;
  }

  if (Object.prototype.hasOwnProperty.call(patch, "transportType")) {
    patch.transportType = normalizeTransportType(patch.transportType);
  }

  if (Object.prototype.hasOwnProperty.call(patch, "workMix")) {
    patch.workMix = normalizeWorkMix(patch.workMix);
  }

  if (Object.prototype.hasOwnProperty.call(patch, "status")) {
    patch.status = normalizeStatus(patch.status);
  }

  if (Object.prototype.hasOwnProperty.call(patch, "lifecycle")) {
    patch.lifecycle = normalizeLifecycle(patch.lifecycle);
  }

  if (Object.prototype.hasOwnProperty.call(patch, "partsStatus")) {
    patch.partsStatus = normalizePartsStatus(patch.partsStatus);
  }

  if (Object.prototype.hasOwnProperty.call(patch, "partsActionStatus")) {
    patch.partsActionStatus = normalizePartsActionStatus(patch.partsActionStatus);
  }

  if (Object.prototype.hasOwnProperty.call(patch, "partsRequestType")) {
    patch.partsRequestType = safeString(patch.partsRequestType).toUpperCase();
  }

  if (Object.prototype.hasOwnProperty.call(patch, "partsEtaISO")) {
    patch.partsEtaISO = safeString(patch.partsEtaISO).trim();
  }

  if (Object.prototype.hasOwnProperty.call(patch, "customerContacted")) {
    patch.customerContacted =
      patch.customerContacted === true ||
      safeString(patch.customerContacted).toLowerCase().trim() === "true";
  }

  if (Object.prototype.hasOwnProperty.call(patch, "customerContactNote")) {
    patch.customerContactNote = safeString(patch.customerContactNote);
  }

  if (Object.prototype.hasOwnProperty.call(patch, "customerContactedAt")) {
    patch.customerContactedAt = safeString(patch.customerContactedAt).trim();
  }

  if (Object.prototype.hasOwnProperty.call(patch, "customerContactedByRole")) {
    patch.customerContactedByRole = safeString(patch.customerContactedByRole).trim();
  }

  if (Object.prototype.hasOwnProperty.call(patch, "customerContactedByName")) {
    patch.customerContactedByName = safeString(patch.customerContactedByName).trim();
  }

  if (Object.prototype.hasOwnProperty.call(patch, "manualFlag")) {
    patch.manualFlag = safeString(patch.manualFlag).trim();
  }

  if (Object.prototype.hasOwnProperty.call(patch, "manualFlagTone")) {
    patch.manualFlagTone = safeString(patch.manualFlagTone).trim().toLowerCase();
  }

  if (Object.prototype.hasOwnProperty.call(patch, "manualFlagSetAt")) {
    patch.manualFlagSetAt = safeString(patch.manualFlagSetAt).trim();
  }

  if (Object.prototype.hasOwnProperty.call(patch, "manualFlagSetByRole")) {
    patch.manualFlagSetByRole = safeString(patch.manualFlagSetByRole).trim();
  }

  if (Object.prototype.hasOwnProperty.call(patch, "manualFlagSetByName")) {
    patch.manualFlagSetByName = safeString(patch.manualFlagSetByName).trim();
  }

  if (Object.prototype.hasOwnProperty.call(patch, "timestamps")) {
    patch.timestamps =
      patch.timestamps && typeof patch.timestamps === "object" ? patch.timestamps : {};
  }

  return patch;
}

const READ_ONLY_RO_PATCH_FIELDS = new Set([
  "assignedAt",
  "startedAt",
  "waitingPartsAt",
  "completedAt",
  "archivedAt",
  "workStartAt",
  "workEndAt",
  "breakStartAt",
  "breakEndAt",
]);

const READ_ONLY_TIMESTAMP_PATCH_FIELDS = new Set([
  "startedAt",
  "waitingPartsAt",
  "breakStartAt",
  "breakEndAt",
  "workedSeconds",
  "breakSeconds",
  "sessionBreakSeconds",
]);

export function stripReadOnlyRoPatchFields(rawPatch) {
  const patch = rawPatch && typeof rawPatch === "object" ? { ...rawPatch } : {};

  for (const key of READ_ONLY_RO_PATCH_FIELDS) {
    delete patch[key];
  }

  if (patch.timestamps && typeof patch.timestamps === "object" && !Array.isArray(patch.timestamps)) {
    patch.timestamps = Object.fromEntries(
      Object.entries(patch.timestamps).filter(
        ([key]) => !READ_ONLY_TIMESTAMP_PATCH_FIELDS.has(key)
      )
    );
  }

  return patch;
}

export function alignLifecycleAndStatus(existingRO, rawPatch) {
  const ro = existingRO ? normalizeRO(existingRO) : null;
  const patch = canonicalizePatch(rawPatch);

  const hasLc = Object.prototype.hasOwnProperty.call(patch, "lifecycle");
  const hasSt = Object.prototype.hasOwnProperty.call(patch, "status");

  let lifecycle = ro?.lifecycle || RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;

  if (hasLc) lifecycle = normalizeLifecycle(patch.lifecycle);

  if (!hasLc && hasSt) {
    const st = normalizeStatus(patch.status);

    if (st === RO_STATUS.COMPLETED) lifecycle = RO_LIFECYCLE.COMPLETED;
    else if (st === RO_STATUS.AVAILABLE) lifecycle = RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;
    else if (st === RO_STATUS.ASSIGNED) lifecycle = RO_LIFECYCLE.OPEN;
    else if (st === RO_STATUS.IN_PROGRESS) lifecycle = RO_LIFECYCLE.IN_PROGRESS;
    else if (st === "ON_HOLD" || st === "VEHICLE_GONE") lifecycle = RO_LIFECYCLE.OPEN;
  }

  const nextStatus = statusFromLifecycle(lifecycle, {
    ...(ro || {}),
    ...(patch || {}),
    lifecycle,
  });

  if (
    Object.prototype.hasOwnProperty.call(patch, "status") &&
    (patch.status === "ON_HOLD" || patch.status === "VEHICLE_GONE")
  ) {
    return { ...patch, lifecycle, status: patch.status };
  }

  return { ...patch, lifecycle, status: nextStatus };
}

export function ensureWorkFields(t0) {
  const t = { ...(t0 || {}) };
  return {
    ...t,
    workedSeconds: safeNumber(t.workedSeconds) ?? 0,
    breakSeconds: safeNumber(t.breakSeconds) ?? 0,
    sessionBreakSeconds: safeNumber(t.sessionBreakSeconds) ?? 0,
    workSessions: Array.isArray(t.workSessions) ? t.workSessions : [],
  };
}
